import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Node } from './global.type'
import { Text } from '~core/components'
import { getNodesMap } from '~editor/selectors/nodes'
import {
  preprocessActionsToEvents,
  preprocessElementProps,
} from './utils/processors'
import { useTheme } from '~/chakra'
import { useRouter } from 'next/router'
import { useDispatch } from '~editor/hooks/useDispatch'
import { useSelector } from '~editor/hooks/useSelector'
import { useActions } from '~/core/utils/processors'
import { useElement } from '~core/registry'
import { getStateProps } from '~/core/utils/getStateProps'
import { merge } from 'lodash'
import { ROOT_ID } from '~editor/utils/constants'
import { eventEmitter } from '~editor/events'
import { Listener } from 'eventemitter2'
import { shallowEqual, useStore } from 'react-redux'
import { RootState } from '~editor/store'
import { ComponentTag } from '~core/forge'
import { isEditor } from '~core/utils/isEditor'
import { Clock, Image } from '~core/components/embeds/embeds'
import { logger } from '~core/logger/logger'

// wraps the actual rendernode so we can hide and show it while keeping some functionality
export const RenderNode = ({
  node,
  ...rest
}: { node: Node } & PropsWithChildren) => {
  const isMounted = useRef(false)
  const [visible, setVisible] = useState<boolean>(
    node?.id === ROOT_ID || (node?.visible ?? true),
  )
  const [localState, setLocalState] = useState<string | null | undefined>(
    node?.localState || null,
  )
  const [state, setState] = useState<string | null | undefined>(
    node?.state || null,
  )

  const runActions = useActions(undefined, node)
  const store = useStore()

  useEffect(() => {
    if (!node.id || node.id === ROOT_ID) {
      return
    }
    const unsubVisible = eventEmitter.on(
      `${node.id}.visible`,
      (visibility?: { visible: boolean }) => {
        if (visibility) {
          setVisible(visibility.visible)
          return
        }
        setVisible((shouldRender) => !shouldRender)
      },
      {
        objectify: true,
      },
    )

    const unsubState = eventEmitter.on(
      `${node.id}.state`,
      (stateEvent) => {
        if (stateEvent !== undefined) {
          setState(stateEvent.state)
          return
        }

        const changedNode = getNodesMap(store.getState() as RootState)[node.id]
        setState(changedNode.state)
      },
      { objectify: true },
    )

    const unsubLocal = eventEmitter.on(
      `${node.id}.localState`,
      (state) => {
        if (state !== undefined) {
          setLocalState(state)
          return
        }

        const changedNode = getNodesMap(store.getState() as RootState)[node.id]
        setLocalState(changedNode.localState)
      },
      { objectify: true },
    )

    return () => {
      ;(unsubVisible as Listener).off()
      ;(unsubState as Listener).off()
      ;(unsubLocal as Listener).off()
    }
  }, [node.id])

  useEffect(() => {
    if (!node.id || !visible || node.id === ROOT_ID) {
      return
    }

    // DO NOT REMOVE - this is necessary to not run actions on a component freshly mounted, only do it on user input
    if (!isMounted.current) {
      isMounted.current = true
      return
    }

    if (
      (!state || state === 'default') &&
      node?.actions?.OnStateDefault?.length
    ) {
      void runActions(node.actions.OnStateDefault)
    }

    if (state === 'active' && node?.actions?.OnStateActive?.length) {
      void runActions(node.actions.OnStateActive)
    }
  }, [node.id, visible, state])

  // really shit way of hiding a component/toggling
  if (!node?.id || !visible) {
    return null
  }

  return (
    <RenderNodeInner
      setLocalState={setLocalState}
      node={{ ...node, state, localState }}
      {...rest}
    />
  )
}

const RenderNodeInner = ({
  node,
  setLocalState,
  isPreview = false,
  children,
  ...forwardedProps
}: {
  node: Node
  setLocalState: (value: string | null | undefined) => void
  isPreview?: boolean
  children?: any
}) => {
  const theme = useTheme()
  const router = useRouter()
  const Element = useElement(node?.type)

  const dispatch = useDispatch()
  const { localState, state } = node || {}

  const shardId = useSelector(({ shard }) => shard.shard.id || null)

  const shardName = useSelector(
    ({ shard }) => shard.shard.name || null,
    shallowEqual,
  )
  const propsWithEvents = useMemo(() => {
    return preprocessActionsToEvents(
      node,
      router,
      isEditor,
      dispatch,
      shardName,
    )
  }, [node.props, shardId])

  const newParsedProps = useMemo(() => {
    const defaultStateProps: Record<string, any> = {}

    if (!Element) {
      return {}
    }

    Object.keys(Element.config.states).forEach((key) => {
      defaultStateProps[`_${key}`] = {}
    })

    const defaultProps: Record<string, any> = {
      // TODO: These should be in a node context. Together with node id and other node related stuff
      actions: node?.actions,
      // we set these here because we don't need to save them in data, just during rendering, and they're the same for all components
      top: 0,
      left: 0,
      bottom: 'auto',
      right: 'auto',
      position: 'absolute',
      key: node?.id,
      id: node?.id,
      nodeId: node?.id,
      isPreview: false,
      _default: node?.props,
      setLocalState,
      componentType: Element.config.id,
    }

    const props = merge(
      defaultProps,
      defaultStateProps,
      propsWithEvents,
      Element.config.props,
      node?.props,
      forwardedProps,
    )
    return preprocessElementProps(props, theme)
  }, [node.props, shardId])

  // sets the _active which makes stuff work
  const stateProps = preprocessElementProps(
    getStateProps(node?.props, localState || state),
    theme,
  )

  if (!Element) {
    logger.warn(`type ${node.type} does not exists`)

    const props = {
      transform: { position: { x: '0px', y: '0px' }, rotation: '0deg' },
      ...node.props,
    }

    return (
      <Text.component {...props} id={node.id} fontSize={'14px'}>
        Missing component, please remove
      </Text.component>
    )
  }

  let _children = newParsedProps.children || children

  if (stateProps.children) {
    _children = stateProps.children
  }
  const excludePointerEvents =
    Element.config?.tags.some((t) =>
      [
        ComponentTag.DECORATION,
        ComponentTag.DIVIDER,
        ComponentTag.PANEL,
        ComponentTag.LABEL,
        ComponentTag.GAUGE,
      ].includes(t),
    ) || [Image.config.id, Clock.config.id].includes(Element.config?.id)
  const pointerEvents = excludePointerEvents ? 'none' : undefined

  return React.createElement(
    Element.component,
    {
      ...newParsedProps,
      ...stateProps,
      state: localState || state,
      pointerEvents,
    },
    [_children],
  )
}
